/*!
 * 1DS JS SDK POST plugin, 2.4.7
 * Copyright (c) Microsoft and contributors. All rights reserved.
 * (Microsoft Internal Only)
 */
/**
* Serializer.ts
* @author Abhilash Panwar (abpanwar) Hector Hernandez(hectorh)
* @copyright Microsoft 2018
*/
import { getTenantId, getCommonSchemaMetaData, isArray, CoreUtils, sanitizeProperty, arrForEach, objKeys, doPerf, isValueAssigned } from '@microsoft/1ds-core-js';
import { EventBatch } from './EventBatch';
import dynamicProto from '@microsoft/dynamicproto-js';
var RequestSizeLimitBytes = 3984588; //approx 3.8 Mb
var BeaconRequestSizeLimitBytes = 65000; // approx 64kb (the current Edge, Firefox and Chrome max limit)
var MaxRecordSize = 2000000; //approx 2 Mb
var MaxBeaconRecordSize = Math.min(MaxRecordSize, BeaconRequestSizeLimitBytes);
/**
* Class to handle serialization of event and request.
* Currently uses Bond for serialization. Please note that this may be subject to change.
*/
var Serializer = /** @class */ (function () {
    function Serializer(perfManager) {
        dynamicProto(Serializer, this, function (_self) {
            _self.createPayload = function (retryCnt, isTeardown, isSync, useSendBeacon) {
                return {
                    apiKeys: [],
                    payloadBlob: '',
                    overflow: null,
                    sizeExceed: [],
                    batches: [],
                    numEvents: 0,
                    retryCnt: retryCnt,
                    isTeardown: isTeardown,
                    isSync: isSync,
                    isBeacon: useSendBeacon
                };
            };
            _self.appendPayload = function (payload, theBatch, maxEventsPerBatch) {
                var canAddEvents = payload && theBatch && !payload.overflow;
                if (canAddEvents) {
                    doPerf(perfManager, function () { return 'Serializer:appendPayload'; }, function () {
                        var theEvents = theBatch.events();
                        var payloadBlob = payload.payloadBlob;
                        var payloadEvents = payload.numEvents;
                        var eventsAdded = false;
                        var sizeExceeded = [];
                        var isBeaconPayload = payload.isBeacon;
                        var requestMaxSize = isBeaconPayload ? BeaconRequestSizeLimitBytes : RequestSizeLimitBytes;
                        var recordMaxSize = isBeaconPayload ? MaxBeaconRecordSize : MaxRecordSize;
                        var lp = 0;
                        while (lp < theEvents.length) {
                            var theEvent = theEvents[lp];
                            if (theEvent) {
                                if (payloadEvents >= maxEventsPerBatch) {
                                    // Maximum events per payload reached, so don't add any more
                                    payload.overflow = theBatch.split(lp);
                                    break;
                                }
                                var eventBlob = _self.getEventBlob(theEvent);
                                if (eventBlob.length <= recordMaxSize) {
                                    // This event will fit into the payload
                                    var blobLength = eventBlob.length;
                                    var currentSize = payloadBlob.length;
                                    if (currentSize + blobLength > requestMaxSize) {
                                        // Request or batch size exceeded, so don't add any more to the payload
                                        payload.overflow = theBatch.split(lp);
                                        break;
                                    }
                                    if (payloadBlob) {
                                        payloadBlob += '\n';
                                    }
                                    payloadBlob += eventBlob;
                                    eventsAdded = true;
                                    payloadEvents++;
                                }
                                else {
                                    // Single event size exceeded so remove from the batch
                                    sizeExceeded.concat(theEvents.slice(lp, 1));
                                }
                            }
                            lp++;
                        }
                        if (sizeExceeded && sizeExceeded.length > 0) {
                            payload.sizeExceed.push(EventBatch.create(theBatch.iKey(), sizeExceeded));
                            // Remove the exceeded events from the batch
                        }
                        if (eventsAdded) {
                            payload.batches.push(theBatch);
                            payload.payloadBlob = payloadBlob;
                            payload.numEvents = payloadEvents;
                            var apiKey = theBatch.iKey();
                            if (CoreUtils.arrIndexOf(payload.apiKeys, apiKey) === -1) {
                                payload.apiKeys.push(apiKey);
                            }
                        }
                    }, function () { return ({ payload: payload, theBatch: { iKey: theBatch.iKey(), evts: theBatch.events() }, max: maxEventsPerBatch }); });
                }
                return canAddEvents;
            };
            _self.getEventBlob = function (eventData) {
                return doPerf(perfManager, function () { return 'Serializer.getEventBlob'; }, function () {
                    var data = 'data';
                    var baseData = 'baseData';
                    var ext = 'ext';
                    var serializedEvent = {};
                    serializedEvent['name'] = eventData.name;
                    serializedEvent['time'] = eventData.time;
                    serializedEvent['ver'] = eventData.ver;
                    serializedEvent['iKey'] = 'o:' + getTenantId(eventData.iKey);
                    //part A
                    if (eventData.ext) {
                        serializedEvent[ext] = {};
                        arrForEach(objKeys(eventData.ext), function (key) {
                            serializedEvent[ext][key] = {};
                            var rootKey = 'ext.' + key;
                            arrForEach(objKeys(eventData.ext[key]), function (subKey) {
                                // Only add populated properties
                                if (isValueAssigned(eventData.ext[key][subKey])) {
                                    serializedEvent[ext][key][subKey] = sanitizeProperty(subKey, eventData.ext[key][subKey]).value;
                                }
                            });
                        });
                    }
                    serializedEvent[data] = {};
                    serializedEvent[data][baseData] = {};
                    serializedEvent[data]['baseType'] = eventData.baseType;
                    //part B
                    if (eventData.baseData) {
                        arrForEach(objKeys(eventData.baseData), function (key) {
                            var value = sanitizeProperty(key, eventData.baseData[key]);
                            _addJSONProperty(serializedEvent[data][baseData], key, value);
                            _addJSONPropertyMetaData(serializedEvent[ext], baseData + '.' + key, value);
                        });
                    }
                    //part C
                    if (eventData.data) {
                        arrForEach(objKeys(eventData.data), function (key) {
                            var value = sanitizeProperty(key, eventData.data[key]);
                            _addJSONProperty(serializedEvent[data], key, value);
                            _addJSONPropertyMetaData(serializedEvent[ext], key, value);
                        });
                    }
                    return JSON.stringify(serializedEvent);
                }, function () { return ({ item: eventData }); });
            };
        });
    }
// Removed Stub for Serializer.prototype.createPayload.
// Removed Stub for Serializer.prototype.appendPayload.
// Removed Stub for Serializer.prototype.getEventBlob.
    return Serializer;
}());
export { Serializer };
/**
 * @ignore
 */
function _addJSONProperty(json, propertyKey, propertyValue) {
    if (propertyValue) {
        var jsonToPutProperty = json;
        var splitKey = propertyKey.split('.');
        for (var i = 0; i < splitKey.length - 1; ++i) {
            if (!jsonToPutProperty[splitKey[i]]) {
                jsonToPutProperty[splitKey[i]] = {};
            }
            jsonToPutProperty = jsonToPutProperty[splitKey[i]];
        }
        jsonToPutProperty[splitKey[splitKey.length - 1]] = propertyValue.value;
    }
}
/**
 * @ignore
 */
function _addJSONPropertyMetaData(json, propertyKey, propertyValue) {
    if (propertyValue) {
        var metadata = 'metadata';
        var f = 'f';
        var encodedTypeValue = getCommonSchemaMetaData(propertyValue.value, propertyValue.kind, propertyValue.propertyType);
        if (encodedTypeValue > -1) {
            //Add the metadata
            var jsonToPutMetadata = json;
            if (!jsonToPutMetadata[metadata]) {
                jsonToPutMetadata[metadata] = {};
            }
            if (!jsonToPutMetadata[metadata][f]) {
                jsonToPutMetadata[metadata][f] = {};
            }
            jsonToPutMetadata = jsonToPutMetadata[metadata][f];
            var splitKey = propertyKey.split('.');
            for (var i = 0; i < splitKey.length - 1; ++i) {
                if (!jsonToPutMetadata[splitKey[i]]) {
                    jsonToPutMetadata[splitKey[i]] = {
                        f: {}
                    };
                }
                jsonToPutMetadata = jsonToPutMetadata[splitKey[i]][f];
            }
            if (isArray(propertyValue.value)) {
                jsonToPutMetadata[splitKey[splitKey.length - 1]] = {
                    'a': {
                        't': encodedTypeValue
                    }
                };
            }
            else {
                jsonToPutMetadata[splitKey[splitKey.length - 1]] = {
                    't': encodedTypeValue
                };
            }
        }
    }
}
//# sourceMappingURL=Serializer.js.map